import React, { useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/FirebaseConfig';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery,
} from '@mui/material';

const QRScanner = () => {
  const [uid, setUid] = useState('');
  const [guest, setGuest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ open: false, text: '', severity: 'info' });
  const [scannerRunning, setScannerRunning] = useState(false);
  const scannerRef = React.useRef(null);
  const [scanStatus, setScanStatus] = useState('');
  // no manual camera selector: scanner will auto-select a suitable camera
  // isStaff: main staff (can redeem)
  // isTempStaff: temporary staff (cannot redeem)
  const [isStaff, setIsStaff] = useState(false);
  const [isTempStaff, setIsTempStaff] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setUid(id);
      fetchGuest(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // detect staff users (email contains @staff.com) and set flag
  useEffect(() => {
    try {
      const auth = getAuth();
      const u = auth.currentUser;
      const emailIs = (email) => (email || '').toLowerCase();
      const checkFlags = (emailStr) => {
        const e = emailIs(emailStr);
        return {
          staff: e.includes('@staff.com'),
          tempstaff: e.includes('@tempstaff.com'),
        };
      };
      if (u && u.email) {
        const flags = checkFlags(u.email);
        setIsStaff(flags.staff);
        setIsTempStaff(flags.tempstaff);
      } else {
        // listen for changes just in case
        const unsub = onAuthStateChanged(auth, (nu) => {
          if (nu && nu.email) {
            const flags = checkFlags(nu.email);
            setIsStaff(flags.staff);
            setIsTempStaff(flags.tempstaff);
          } else {
            setIsStaff(false);
            setIsTempStaff(false);
          }
        });
        return () => unsub();
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current && scannerRef.current.clear) {
        try {
          scannerRef.current.clear();
        } catch (e) {}
      }
    };
  }, []);

  const fetchGuest = async (id) => {
    setLoading(true);
    try {
      const ref = doc(db, 'guests', id);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        setGuest(null);
        setMessage({ open: true, text: 'Guest not found', severity: 'error' });
      } else {
        setGuest({ id: snap.id, ...snap.data() });
      }
    } catch (err) {
      console.error(err);
      setMessage({ open: true, text: 'Failed to fetch guest', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = () => {
    if (!uid) return setMessage({ open: true, text: 'Enter UID or scan a QR', severity: 'warning' });
    fetchGuest(uid.trim());
  };

  const handleClear = async () => {
    // stop scanner if running and clear states
    try {
      if (scannerRef.current && scannerRef.current.clear) {
        await scannerRef.current.clear();
      }
    } catch (e) {}
    scannerRef.current = null;
    setScannerRunning(false);
    setUid('');
    setGuest(null);
    setMessage({ open: true, text: 'Cleared', severity: 'info' });
  };

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      navigate('/login');
    } catch (err) {
      console.error('Logout failed', err);
      setMessage({ open: true, text: 'Logout failed', severity: 'error' });
    }
  };

  const startScanner = async () => {
    if (scannerRunning) return;
    try {
      const mod = await import('html5-qrcode');
      const { Html5Qrcode } = mod;
      const id = 'html5qr-reader';
      // clear any previous
      try {
        const container = document.getElementById(id);
        if (container) container.innerHTML = '';
      } catch (e) {}

      // create Html5Qrcode instance which allows starting with a specific cameraId
      const html5QrCode = new Html5Qrcode(id);
      scannerRef.current = html5QrCode;

      // Enumerate cameras and attempt to start on a best candidate. If deviceIds exist, try
      // preferred rear camera first, then fall back to others. If all deviceId attempts fail,
      // try using facingMode: 'environment'. This makes it robust without showing a dropdown.
      setScanStatus('Requesting camera permissions...');
      let cams = [];
      try {
        if (Html5Qrcode.getCameras) {
          cams = await Html5Qrcode.getCameras();
        }
      } catch (e) {
        // permission denied or enumeration failed — we'll fallback below
        cams = [];
      }
  // don't persist cameras — we won't show a manual dropdown

  const candidates = [];
      if (Array.isArray(cams) && cams.length) {
        // prefer labels that indicate back/rear/environment
        const preferred = cams.find(c => /back|rear|environment|wide|outer/i.test(c.label));
        if (preferred) candidates.push(preferred.id);
        // push all others (avoid duplicate)
        cams.forEach(c => { if (!candidates.includes(c.id)) candidates.push(c.id); });
      }

  // helper to attempt start with given camera param
      const parseDecodedText = (text) => {
        if (!text) return '';
        const t = String(text).trim();
        // try query param id
        try {
          // try as URL first
          let u = null;
          try { u = new URL(t); } catch (e) { /* ignore */ }
          if (u) {
            const qid = u.searchParams.get('id');
            if (qid) return decodeURIComponent(qid);
            const path = u.pathname || '';
            if (path) {
              const seg = path.split('/').filter(Boolean).pop();
              if (seg) return decodeURIComponent(seg);
            }
          }
        } catch (e) {
          // ignore
        }
        // fallback: look for id= in raw string
        const m = t.match(/[?&]id=([^&\s]+)/);
        if (m && m[1]) return decodeURIComponent(m[1]);
        // fallback: if contains slashes, take last segment
        if (t.includes('/')) {
          const seg = t.split('/').filter(Boolean).pop();
          if (seg) return decodeURIComponent(seg);
        }
        return t;
      };

      // helper to attempt start with given camera param. We'll wait for either a decode
      // or a timeout (8s). If timeout happens we stop that camera and return false so
      // the caller can try the next candidate.
      const tryStart = async (cameraParam) => {
        setScanStatus(typeof cameraParam === 'string' ? `Starting camera ${cameraParam}...` : `Starting camera (${JSON.stringify(cameraParam)})...`);
        let decodeResolved = false;
        return new Promise(async (resolve) => {
          try {
            // compute qrbox relative to container width for better detection
            const container = document.getElementById(id);
            const containerW = (container && container.clientWidth) ? container.clientWidth : 400;
            const qrbox = Math.max(200, Math.min(420, Math.floor(containerW * 0.7)));
            await html5QrCode.start(
              cameraParam,
              { fps: 15, qrbox },
              (decodedText) => {
                // decoded
                decodeResolved = true;
                console.log('QR decoded (raw):', decodedText);
                const parsed = parseDecodedText(decodedText);
                console.log('QR parsed id:', parsed);
                setUid(parsed);
                setScanStatus(`Decoded: ${parsed}`);
                fetchGuest(parsed).catch((e) => { console.error('fetchGuest error after scan', e); });
                // stop and clear
                html5QrCode.stop().catch(() => {}).finally(() => {
                  try { html5QrCode.clear(); } catch (e) {}
                  scannerRef.current = null;
                  setScannerRunning(false);
                  setScanStatus('');
                });
                resolve(true);
              },
              (errorMessage) => {
                // frame-level errors are useful for debugging — log them
                console.debug('frame scan error:', errorMessage);
              }
            );
            console.log('html5QrCode.start resolved for', cameraParam);
            // mark running immediately after start succeeds
            scannerRef.current = html5QrCode;
            setScannerRunning(true);
              setScanStatus('Scanning...');
          } catch (startErr) {
            console.warn('start failed for', cameraParam, startErr && startErr.message ? startErr.message : startErr);
            setScanStatus(startErr && startErr.message ? `Start error: ${startErr.message}` : 'Start error');
            resolve(false);
            return;
          }

          // if no decode within timeout, stop this attempt and resolve false so next candidate is tried
          const ATTEMPT_TIMEOUT = 20000; // ms
          const to = setTimeout(async () => {
            if (!decodeResolved) {
              try {
                await html5QrCode.stop();
              } catch (e) {}
              try { html5QrCode.clear(); } catch (e) {}
              setScanStatus('No decode — stopped');
              resolve(false);
            }
            clearTimeout(to);
          }, ATTEMPT_TIMEOUT);
        });
      };
      // Choose a single camera candidate (no automatic retry). Prefer the detected preferred
      // deviceId if available; otherwise fall back to facingMode: 'environment'.
      const cameraToTry = (Array.isArray(candidates) && candidates.length) ? candidates[0] : { facingMode: 'environment' };
      const started = await tryStart(cameraToTry);
      if (!started) {
        setMessage({ open: true, text: 'Unable to start camera or no QR decoded. Check permissions or try another browser.', severity: 'error' });
        setScanStatus('Camera start failed or no decode');
  // manual camera selection removed; rely on auto-selection only
        scannerRef.current = null;
        setScannerRunning(false);
        return;
      }
      // if started successfully, keep instance reference
      scannerRef.current = html5QrCode;
      setScannerRunning(true);
      setScanStatus('Scanning...');
    } catch (err) {
      console.error('QR scanner import failed', err);
      setMessage({ open: true, text: "Scanner not installed. Run 'npm install html5-qrcode'", severity: 'warning' });
    }
  };

  const stopScanner = async () => {
    if (!scannerRunning || !scannerRef.current) return;
    try {
      // Html5Qrcode exposes stop(); Html5QrcodeScanner exposes clear(); try both
      if (typeof scannerRef.current.stop === 'function') {
        await scannerRef.current.stop();
      }
      if (typeof scannerRef.current.clear === 'function') {
        await scannerRef.current.clear();
      }
    } catch (e) {
      // ignore
    }
    scannerRef.current = null;
    setScannerRunning(false);
  };

  const saveUpdates = async (updates) => {
    if (!guest || !guest.id) return;
    setSaving(true);
    try {
      const ref = doc(db, 'guests', guest.id);
      await updateDoc(ref, updates);
      const snap = await getDoc(ref);
      setGuest({ id: snap.id, ...snap.data() });
      setMessage({ open: true, text: 'Updated', severity: 'success' });
    } catch (err) {
      console.error(err);
      setMessage({ open: true, text: 'Update failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const checkIn = () => {
    // open confirm dialog for check in
    if (!guest) return;
    if (guest.is_arrived) return;
    openConfirm('checkIn');
  };

  const performCheckIn = async () => {
    if (!guest) return;
    const paxCount = Math.max(1, Number(guest.pax) || 1);
    const updates = { is_arrived: true, is_confirmed: true };
    await saveUpdates(updates);
    setMessage({ open: true, text: `Checked in ${paxCount} pax`, severity: 'success' });
  };
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const openConfirm = (action) => {
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const performUseToken = async () => {
    if (!guest) return;
    const current = Number(guest.token) || 0;
    if (current <= 0) {
      setMessage({ open: true, text: 'No tokens left', severity: 'warning' });
      return;
    }
    await saveUpdates({ token: current - 1 });
    setMessage({ open: true, text: `Token used — ${current - 1} left`, severity: 'success' });
  };

  const performRedeemGift = async () => {
    if (!guest) return;
    const redeemAmt = Number(guest.redeem_amt) || 0;
    const currentToken = Number(guest.token) || 0;
    const initialTokens = (Number(guest.pax) || 0) * 3;
    const used = initialTokens - currentToken;
    if (redeemAmt <= 0) {
      setMessage({ open: true, text: 'No redeemable gifts left', severity: 'warning' });
      return;
    }
    // Determine unlocked redeem slots based on tokens used (one slot per 3 tokens used)
    const unlockedSlots = Math.floor(used / 3);
    const initialRedeemTotal = Number(guest.pax) || 0; // redeem_amt was initialized to pax
    const redeemedPerformed = initialRedeemTotal - (Number(guest.redeem_amt) || 0);
    const availableSlots = unlockedSlots - redeemedPerformed;
    const allowed = (availableSlots > 0);
    if (!allowed) {
      setMessage({ open: true, text: 'Redeem allowed only after additional 3 tokens have been used', severity: 'warning' });
      return;
    }
    await saveUpdates({ redeem_amt: redeemAmt - 1 });
    setMessage({ open: true, text: `Redeemed gift — ${redeemAmt - 1} left`, severity: 'success' });
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (confirmAction === 'useToken') {
      await performUseToken();
    } else if (confirmAction === 'redeemGift') {
      await performRedeemGift();
    } else if (confirmAction === 'checkIn') {
      await performCheckIn();
    }
    setConfirmAction(null);
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    setConfirmAction(null);
  };

  const handlePaxChange = (val) => {
    // do not allow changing pax after check-in
    if (guest && guest.is_arrived) return;
    // follow guestlist behaviour: parse number and update pax/token/redeem_amt
    const newPax = Number(val);
    setGuest((g) => ({ ...g, pax: newPax, token: newPax * 3, redeem_amt: newPax }));
  };

  const handlePaxSave = async () => {
    if (!guest || !guest.id) return;
    // do not save pax if already checked in
    if (guest.is_arrived) return;
    // when saving, ensure pax is a number and at least 1 as a fallback
    const parsed = Number(guest.pax);
    const newPax = Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
    const updates = { pax: newPax, token: newPax * 3, redeem_amt: newPax };
    await saveUpdates(updates);
  };

  const handleValueChange = (val) => {
    // update local guest state immediately; allow empty/NaN while typing
    const parsed = Number(val);
    setGuest((g) => ({ ...g, value: Number.isFinite(parsed) ? parsed : val }));
  };

  const handleValueSave = async () => {
    if (!guest || !guest.id) return;
    const parsed = Number(guest.value);
    const newValue = Number.isFinite(parsed) ? parsed : 0;
    await saveUpdates({ value: newValue });
  };

  // compute redeem availability based on pax*3 initial tokens and tokens used in groups of 3
    const initialTokens = guest ? (Number(guest.pax) || 0) * 3 : 0;
    const currentTokens = guest ? (Number(guest.token) || 0) : 0;
    const usedTokens = initialTokens - currentTokens;
    const hasRedeemAmt = guest ? (Number(guest.redeem_amt) || 0) > 0 : false;
    const unlockedSlots = Math.floor(usedTokens / 3);
    const initialRedeemTotal = guest ? (Number(guest.pax) || 0) : 0;
    const redeemedPerformed = initialRedeemTotal - (guest ? (Number(guest.redeem_amt) || 0) : 0);
    const availableRedeemSlots = unlockedSlots - redeemedPerformed;
    const canRedeem = hasRedeemAmt && (availableRedeemSlots > 0);

  return (
    <Box p={3} maxWidth={720} mx="auto">
      <Typography variant="h5" mb={2}>
        QR Scan / Guest Check
      </Typography>

      <Box display="flex" gap={2} mb={2} flexDirection={isMobile ? 'column' : 'row'} alignItems="center">
        <TextField
          label="Guest UID (from QR)"
          value={uid}
          onChange={(e) => setUid(e.target.value)}
          fullWidth
        />
        {/* No camera dropdown — scanner will auto-select a suitable camera */}

        <Box display="flex" gap={1} alignItems="center">
          <Button variant="contained" onClick={handleLoad} disabled={loading}>
            Load
          </Button>
          <Button variant="outlined" onClick={handleClear} disabled={loading}>
            Clear
          </Button>
          {/* Manual camera picker removed — scanner auto-selects */}
          {/* Upload QR Image removed — scanning now relies on live camera only */}
          {!scannerRunning ? (
            <Button variant="outlined" onClick={startScanner} disabled={loading}>
              Start Scan
            </Button>
          ) : (
            <Button variant="outlined" color="error" onClick={stopScanner} disabled={loading}>
              Stop Scan
            </Button>
          )}
        </Box>
      </Box>

      {/* Scanner target - html5-qrcode will render here when started */}
      <div id="html5qr-reader" style={{ width: '100%', marginBottom: 12 }} />
      {scanStatus && scanStatus !== 'Scanning...' && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          {scanStatus}
        </Typography>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : guest ? (
        <Box display="flex" justifyContent="center">
          <Paper sx={{ p: 2, width: isMobile ? '100%' : 520, textAlign: 'center' }}>
            <Typography variant="h6">{guest.name || '—'}</Typography>
            <Typography sx={{ mb: 1 }}>Category: {guest.category || '—'}</Typography>
            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
              <TextField
                label="Pax"
                type="number"
                value={guest.pax ?? 0}
                onChange={(e) => handlePaxChange(e.target.value)}
                disabled={(isStaff || isTempStaff) || guest?.is_arrived}
                onBlur={handlePaxSave}
                onKeyDown={(e) => { if (e.key === 'Enter') handlePaxSave(); }}
                inputProps={{ min: 1 }}
                sx={{ width: isMobile ? '100%' : 140 }}
              />
              <Box display="flex" gap={2} flexDirection={isMobile ? 'column' : 'row'} alignItems="center">
                <Typography>Token: <strong>{guest.token ?? 0}</strong></Typography>
                <Typography>Redeem Amt: <strong>{guest.redeem_amt ?? 0}</strong></Typography>
              </Box>
              {!(isStaff || isTempStaff) && (
                <TextField
                  label="Value"
                  type="number"
                  value={guest.value ?? 0}
                  onChange={(e) => handleValueChange(e.target.value)}
                  onBlur={handleValueSave}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleValueSave(); }}
                  inputProps={{ min: 0 }}
                  sx={{ width: isMobile ? '100%' : 140 }}
                />
              )}
              <Typography>Table No: {guest.table_no ?? '—'}</Typography>
              <Typography>Arrived: {guest.is_arrived ? 'Yes' : 'No'}</Typography>
            </Box>
            <Box display="flex" gap={2} mt={2} justifyContent="center" flexWrap="wrap">
              {!isStaff && !isTempStaff && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={checkIn}
                  disabled={saving || guest.is_arrived}
                >
                  {guest.is_arrived ? 'Checked In' : 'Check In'}
                </Button>
              )}
              <Button
                variant="outlined"
                color="primary"
                onClick={() => openConfirm('useToken')}
                disabled={saving || (Number(guest.token) || 0) <= 0}
              >
                Use Token (-1)
              </Button>
              {!isTempStaff && (
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => openConfirm('redeemGift')}
                  disabled={saving || !canRedeem}
                >
                  Redeem Gift (-1)
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      ) : (
        <Typography>No guest loaded. Enter UID or scan a QR.</Typography>
      )}

      <Snackbar
        open={message.open}
        autoHideDuration={3000}
        onClose={() => setMessage((m) => ({ ...m, open: false }))}
      >
        <Alert severity={message.severity} onClose={() => setMessage((m) => ({ ...m, open: false }))}>
          {message.text}
        </Alert>
      </Snackbar>
      <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
        <Button variant="outlined" color="secondary" onClick={handleLogout}>
          Logout
        </Button>
      </Box>
      <Dialog open={confirmOpen} onClose={handleCancelConfirm} fullWidth>
        <DialogTitle>
          {confirmAction === 'useToken' ? 'Confirm Use Token' : confirmAction === 'redeemGift' ? 'Confirm Redeem Gift' : confirmAction === 'checkIn' ? 'Confirm Check In' : 'Confirm'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === 'useToken' && (
              <>
                Use 1 token for <strong>{guest?.name || 'this guest'}</strong>?
                <br />
                Current tokens: <strong>{Number(guest?.token) || 0}</strong>. After: <strong>{Math.max((Number(guest?.token) || 0) - 1, 0)}</strong>.
              </>
            )}
            {confirmAction === 'redeemGift' && (
              <>
                Redeem 1 gift for <strong>{guest?.name || 'this guest'}</strong>?
                <br />
                Current redeem amount: <strong>{Number(guest?.redeem_amt) || 0}</strong>. After: <strong>{Math.max((Number(guest?.redeem_amt) || 0) - 1, 0)}</strong>.
              </>
            )}
            {confirmAction === 'checkIn' && (
              <>
                Check in <strong>{guest?.name || 'this guest'}</strong> for <strong>{Number(guest?.pax) || 0}</strong> pax?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm}>Cancel</Button>
          <Button onClick={handleConfirm} color="primary" variant="contained" disabled={saving}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QRScanner;
